/** @jsx jsx */
import { FC, useCallback } from 'react';
import { jsx, css } from '@emotion/core';
import useAnalytics, { Event } from 'hooks/useAnalytics';
import useFmcHost from 'hooks/useFmcHost';
import { addToWallet } from 'fmc-sdk';
import useWalletDownload, { WalletError } from 'hooks/requests/wallet';
import catchOnly from 'utils/catchOnly';
import { NonClientError, Status } from 'hooks/requests/fetchUtils';
import { useSnackbar } from 'notistack';
import { CircularProgress } from '@material-ui/core';

interface PropTypes {
  storeNumber: string | null;
  className?: string;
  vendor: 'apple' | 'google';
}

const styles = {
  img: css`
    max-height: 48px;
    max-width: 100%;
  `,
  container: css`
    // fix the height of the containter to match the image.
    // The spinner is shorter than the image, so without this, there is a
    // visible re-layout when switching between image and spinner.
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  button: css`
    height: 48px;
    display: flex;
    background: none;
    border: none;
    align-items: center;
    justify-content: center;
  `,
};

const WalletButton: FC<PropTypes> = ({ storeNumber, className, vendor }) => {
  const { sendEvents } = useAnalytics();
  const fmcDispatch = useFmcHost();
  const [downloadWallet, status] = useWalletDownload();
  const { enqueueSnackbar } = useSnackbar();
  const loading = status === Status.IN_PROGRESS;

  const handleClick = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      if (loading) {
        return;
      }
      fmcDispatch(addToWallet());
      sendEvents([Event.AddToWallet]);
      try {
        await downloadWallet(vendor, storeNumber);
      } catch (e) {
        if (catchOnly(e, NonClientError, WalletError)) {
          enqueueSnackbar('Something has gone wrong. Please try again later.', {
            variant: 'error',
            persist: true,
          });
        }
      }
    },
    [
      loading,
      fmcDispatch,
      sendEvents,
      downloadWallet,
      vendor,
      storeNumber,
      enqueueSnackbar,
    ],
  );

  return (
    <div css={styles.container} className={className}>
      {loading ? (
        <CircularProgress aria-label="progress" size={24} />
      ) : vendor === 'apple' ? (
        <button css={styles.button}>
          <img
            src={
              'https://id-idss.s3.amazonaws.com/assets/add_to_apple_wallet.png'
            }
            css={styles.img}
            alt="Apple Wallet"
            onClick={handleClick}
          />
        </button>
      ) : (
        <button css={styles.button}>
          <img
            src={`https://id-idss.s3.amazonaws.com/assets/add_to_google_pay.png`}
            css={styles.img}
            alt="Google Wallet"
            onClick={handleClick}
          />
        </button>
      )}
    </div>
  );
};

export default WalletButton;
